import { useEffect, useRef, useState, useCallback } from 'react';
import { 
  User, Shield, Briefcase, Wrench, 
  Mail, Lock, CheckCircle2, AlertCircle, Trash2, Edit3, 
  Plus, Search, LayoutGrid, List, Smartphone, X, ChevronRight,
  RefreshCcw, Eye, EyeOff, Save, GripVertical, Camera, Users
} from 'lucide-react';
import { pb, getFileUrl } from '../../lib/pb';
import '../../styles/admin.css';

// --- ICONS MAPPING ---
const ROLE_CONFIG = {
  'admin': { icon: <Shield size={16} />, label: 'Administrateur', color: '#6366f1', bg: '#eef2ff' },
  'commercial': { icon: <Briefcase size={16} />, label: 'Commercial', color: '#f59e0b', bg: '#fffbeb' },
  'worker': { icon: <Wrench size={16} />, label: 'Technicien / Chantier', color: '#10b981', bg: '#f0fdf4' }
};

export default function AdminTeam() {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [viewMode, setViewMode] = useState('grid');
  const [draggingId, setDraggingId] = useState(null);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await pb.collection('team_members').getFullList({
        filter: search ? `name ~ "${search}" || role ~ "${search}"` : '',
        expand: 'user',
        sort: 'order'
      });
      setItems(res);
    } catch (e) {
      setError("Erreur de synchronisation de l'équipe");
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    let unsub = null;
    (async () => {
      try {
        unsub = await pb.collection('team_members').subscribe('*', () => load());
      } catch (e) { console.error(e); }
    })();
    return () => { if (unsub) pb.collection('team_members').unsubscribe('*'); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTogglePublished = async (m) => {
    const oldStatus = m.published;
    setItems(prev => prev.map(item => item.id === m.id ? { ...item, published: !oldStatus } : item));

    try {
      await pb.collection('team_members').update(m.id, { published: !oldStatus });
      setSuccess(!oldStatus ? 'Membre publié sur le site ⭐' : 'Membre masqué du site 🌑');
      setTimeout(() => setSuccess(''), 2000);
    } catch (e) {
      setItems(prev => prev.map(item => item.id === m.id ? { ...item, published: oldStatus } : item));
      setError("Erreur lors de la mise à jour");
    }
  };

  const openCreate = () => setEditing({ name: '', role: '', bio: '', published: true });
  const openEdit = (member) => setEditing(member);

  const handleDragStart = (id) => setDraggingId(id);

  const handleDragOver = (targetId, e) => {
    e.preventDefault();
    if (!draggingId || draggingId === targetId) return;
    setItems(prev => {
      const next = [...prev];
      const fromIdx = next.findIndex(s => s.id === draggingId);
      const toIdx = next.findIndex(s => s.id === targetId);
      if (fromIdx === -1 || toIdx === -1) return prev;
      const [moved] = next.splice(fromIdx, 1);
      next.splice(toIdx, 0, moved);
      return next;
    });
  };

  const persistOrder = async () => {
    if (!items.length) return;
    try {
      const updates = items.map((s, index) => {
        const newOrder = (index + 1) * 10;
        if (s.order !== newOrder) return pb.collection('team_members').update(s.id, { order: newOrder });
        return null;
      }).filter(Boolean);
      if (updates.length) {
        await Promise.all(updates);
        setSuccess('Hiérarchie mise à jour ✨');
        setTimeout(() => setSuccess(''), 2000);
      }
    } catch (e) { setError("Erreur de réorganisation"); }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    persistOrder();
    setDraggingId(null);
  };

  return (
    <div className="admin-main animation-fade-in">
      <header className="admin-header-premium">
        <div className="header-content">
          <div className="header-tag">Ressources Humaines</div>
          <h1>L'Équipe d'Excellence <span className="count-badge">{items.length}</span></h1>
          <p className="header-subtitle">Gérez vos colllaborateurs, leurs accès au planning mobile et leur visibilité publique.</p>
        </div>
        <button className="btn-add-premium" onClick={openCreate}>
          <Plus size={20} />
          <span>Nouvelle Recrue</span>
        </button>
      </header>

      {success && <div className="toast-success"><CheckCircle2 size={18}/> {success}</div>}
      {error && <div className="toast-error"><AlertCircle size={18}/> {error}</div>}

      <div className="admin-controls-bar">
        <div className="search-box-premium">
          <Search size={18} className="search-icon" />
          <input 
            placeholder="Rechercher par nom, métier ou rôle..." 
            value={search} 
            onChange={e => setSearch(e.target.value)}
          />
        </div>

        <div className="filters-and-view">
          <div className="view-switcher-premium">
            <button className={viewMode === 'list' ? 'active' : ''} onClick={() => setViewMode('list')}><List size={18}/></button>
            <button className={viewMode === 'grid' ? 'active' : ''} onClick={() => setViewMode('grid')}><LayoutGrid size={18}/></button>
          </div>
        </div>
      </div>

      {loading && (
        <div className="loading-state">
          <RefreshCcw className="spin" size={32} />
          <p>Synchronisation des profils...</p>
        </div>
      )}

      {!loading && items.length === 0 && (
        <div className="empty-state-card">
          <User size={48} />
          <h3>Seul au monde ?</h3>
          <p>Ajoutez les membres de votre équipe pour commencer à leur assigner des chantiers.</p>
          <button className="btn filled" onClick={openCreate}>Ajouter un membre</button>
        </div>
      )}

      {!loading && items.length > 0 && (
        <div className={`team-content-container ${viewMode}`}>
          {viewMode === 'list' ? (
            <div className="premium-list">
              {items.map((m) => (
                <div 
                  key={m.id} 
                  className={`list-item-row ${draggingId === m.id ? 'dragging' : ''}`}
                  draggable
                  onDragStart={() => handleDragStart(m.id)}
                  onDragOver={(e) => handleDragOver(m.id, e)}
                  onDrop={handleDrop}
                >
                  <div className="drag-handle"><GripVertical size={18}/></div>
                  <div className="item-icon-box rounded">
                    {m.photo ? (
                      <img src={getFileUrl(m, m.photo)} alt={m.name} />
                    ) : (
                      <User size={20} className="placeholder" />
                    )}
                  </div>
                  <div className="item-info">
                    <div className="item-title">{m.name}</div>
                    <div className="item-meta">
                      {m.role || 'Personnel AEF'} • {m.expand?.user?.email || 'Pas d\'email'}
                    </div>
                  </div>
                  <div className="item-status">
                     <button className={`status-pill ${m.published ? 'online' : 'offline'}`} onClick={() => handleTogglePublished(m)}>
                        {m.published ? <Eye size={14}/> : <EyeOff size={14}/>}
                        <span>{m.published ? 'Public' : 'Masqué'}</span>
                     </button>
                  </div>
                  <div className="item-actions">
                    <button className="action-btn edit" onClick={() => openEdit(m)} title="Modifier">
                      <Edit3 size={18}/>
                    </button>
                    <button className="action-btn delete" onClick={() => { if(window.confirm('Supprimer ce membre ?')) { pb.collection('team_members').delete(m.id).then(() => load()); } }} title="Supprimer">
                      <Trash2 size={18}/>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="premium-grid-team">
              {items.map((m) => {
                const conf = ROLE_CONFIG[m.expand?.user?.role] || ROLE_CONFIG.worker;
                return (
                  <div 
                    key={m.id} 
                    className="member-card-premium animation-slide-up"
                    onClick={() => openEdit(m)}
                    draggable
                    onDragStart={() => handleDragStart(m.id)}
                    onDragOver={(e) => handleDragOver(m.id, e)}
                    onDrop={handleDrop}
                  >
                    <div className="member-avatar-box">
                      <div className="avatar-main" style={{ borderColor: conf.color }}>
                         {m.photo ? <img src={getFileUrl(m, m.photo)} alt={m.name} /> : <User size={30} color="#94a3b8" />}
                      </div>
                      <div className="role-tag-icon" style={{ background: conf.color }}>
                         {conf.icon}
                      </div>
                    </div>
                    
                    <div className="member-body">
                      <h3 className="member-name">{m.name}</h3>
                      <p className="member-position">{m.role || 'Collaborateur'}</p>
                      
                      <div className="member-badges">
                         {m.expand?.user ? (
                            <span className="badge-app active"><Smartphone size={12}/> App Connectée</span>
                         ) : (
                            <span className="badge-app none">Accès restreint</span>
                         )}
                         {m.published && <span className="badge-site"><Eye size={12}/> Public</span>}
                      </div>

                      <div className="card-actions-row">
                         <button className={`btn-mini ${m.published ? 'online' : 'offline'}`} onClick={(e) => { e.stopPropagation(); handleTogglePublished(m); }} title={m.published ? 'Masquer' : 'Publier'}>
                            {m.published ? <Eye size={16}/> : <EyeOff size={16}/>}
                         </button>
                         <button className="btn-mini edit" onClick={(e) => { e.stopPropagation(); openEdit(m); }} title="Modifier"><Edit3 size={16}/></button>
                         <button className="btn-mini delete" onClick={(e) => { e.stopPropagation(); if(window.confirm('Supprimer ce membre ?')) { pb.collection('team_members').delete(m.id).then(() => load()); } }} title="Supprimer"><Trash2 size={16}/></button>
                      </div>

                      <div className="member-footer-hint">
                         <Mail size={12}/> {m.expand?.user?.email || 'Non renseigné'}
                         <ChevronRight size={14} className="arrow-hint"/>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {editing && (
        <TeamStudio
          member={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}

      <style>{`
        .premium-grid-team { display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); gap: 25px; padding: 20px 0; }
        
        .member-card-premium { background: #fff; border-radius: 32px; border: 1px solid var(--admin-border); padding: 30px 20px; display: flex; flex-direction: column; align-items: center; text-align: center; transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1); cursor: grab; position: relative; }
        .member-card-premium:hover { transform: translateY(-8px); border-color: var(--admin-primary); box-shadow: 0 20px 40px -15px rgba(0,0,0,0.08); }
        
        .member-avatar-box { position: relative; margin-bottom: 20px; }
        .avatar-main { width: 100px; height: 100px; border-radius: 50%; border: 3px solid #f1f5f9; padding: 4px; background: #fff; overflow: hidden; display: flex; align-items: center; justify-content: center; }
        .avatar-main img { width: 100%; height: 100%; object-fit: cover; border-radius: 50%; }
        
        .role-tag-icon { position: absolute; bottom: 0; right: 0; width: 32px; height: 32px; border-radius: 50%; color: #fff; display: flex; align-items: center; justify-content: center; border: 3px solid #fff; box-shadow: 0 4px 10px rgba(0,0,0,0.15); }
        
        .member-name { font-size: 19px; font-weight: 900; color: #0f172a; margin: 0 0 5px 0; }
        .member-position { font-size: 14px; font-weight: 700; color: #94a3b8; margin: 0 0 15px 0; }
        
        .member-badges { display: flex; gap: 8px; justify-content: center; margin-bottom: 20px; }
        .badge-app { font-size: 10px; font-weight: 800; text-transform: uppercase; padding: 4px 10px; border-radius: 8px; display: flex; align-items: center; gap: 5px; }
        .badge-app.active { background: #ecfdf5; color: #10b981; }
        .badge-app.none { background: #f1f5f9; color: #94a3b8; }
        .badge-site { background: #fffbeb; color: #d97706; font-size: 10px; font-weight: 800; text-transform: uppercase; padding: 4px 10px; border-radius: 8px; display: flex; align-items: center; gap: 5px; }

        .member-footer-hint { margin-top: 10px; padding-top: 15px; border-top: 1px solid #f8fafc; font-size: 11px; color: #cbd5e1; display: flex; align-items: center; gap: 8px; width: 100%; justify-content: center; position: relative; }
        .arrow-hint { position: absolute; right: 0; opacity: 0; transform: translateX(-10px); transition: all 0.3s; }
        .member-card-premium:hover .arrow-hint { opacity: 1; transform: translateX(0); color: var(--admin-primary); }

        .item-icon-box.rounded { border-radius: 50%; width: 44px; height: 44px; }
        .item-icon-box.rounded img { border-radius: 50%; }

        @media (max-width: 600px) {
          .premium-grid-team { grid-template-columns: 1fr; }
          .member-card-premium { flex-direction: row; text-align: left; padding: 20px; }
          .member-avatar-box { margin-bottom: 0; margin-right: 20px; }
          .avatar-main { width: 70px; height: 70px; }
          .member-body { flex: 1; }
          .member-badges { justify-content: flex-start; }
          .member-footer-hint { width: auto; border: none; padding: 0; margin-top: 5px; justify-content: flex-start; }
        }
      `}</style>
    </div>
  );
}

function TeamStudio({ member, onClose, onSaved }) {
  const [activeTab, setActiveTab] = useState('profile');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  
  const [name, setName] = useState(member.name || '');
  const [role, setRole] = useState(member.role || '');
  const [bio, setBio] = useState(member.bio || '');
  const [published, setPublished] = useState(member.published ?? true);
  const [photo, setPhoto] = useState(member.photo ? { type: 'existing', name: member.photo } : null);

  const linkedUser = member.expand?.user || {};
  const [access, setAccess] = useState({
    active: !!member.user,
    email: linkedUser.email || '',
    password: '',
    role: linkedUser.role || 'worker',
    can_view_all_planning: linkedUser.can_view_all_planning || false,
    can_manage_team: linkedUser.can_manage_team || false,
    can_edit_chantiers: linkedUser.can_edit_chantiers || false
  });

  // --- Planning Tab State ---
  const [userData, setUserData] = useState(linkedUser);
  const [assignments, setAssignments] = useState([]);
  const [allChantiers, setAllChantiers] = useState([]);
  const [planningLoading, setPlanningLoading] = useState(false);
  const [formAssign, setFormAssign] = useState({ chantier: '', start_date: '', end_date: '', notes: '' });
  const [showAssignForm, setShowAssignForm] = useState(false);
  const [savingAssign, setSavingAssign] = useState(false);
  const [searchChantier, setSearchChantier] = useState('');

  // --- Link Existing User State ---
  const [existingUsers, setExistingUsers] = useState([]);
  const [userSearchText, setUserSearchText] = useState('');
  const [showUserDropdown, setShowUserDropdown] = useState(false);

  const fetchUsersToLink = async (txt) => {
    setUserSearchText(txt);
    if (txt.length < 2) { setExistingUsers([]); setShowUserDropdown(false); return; }
    try {
      const res = await pb.collection('users').getList(1, 10, {
        filter: `(name ~ "${txt}" || email ~ "${txt}")`,
        sort: 'name'
      });
      setExistingUsers(res.items);
      setShowUserDropdown(true);
    } catch (e) { console.error(e); }
  };

  const handleSelectExistingUser = (u) => {
    setName(u.name || u.username);
    setAccess({ ...access, active: true, email: u.email, role: u.role || 'worker' });
    setUserData(u);
    setShowUserDropdown(false);
    setUserSearchText('');
  };

  const PRESET_COLORS = [
    { name: 'Bleu', value: '#3B82F6' }, { name: 'Émeraude', value: '#10B981' },
    { name: 'Ambre', value: '#F59E0B' }, { name: 'Rose', value: '#F43F5E' },
    { name: 'Violet', value: '#8B5CF6' }, { name: 'Cyan', value: '#06B6D4' },
    { name: 'Orange', value: '#FB923C' }, { name: 'Ardoise', value: '#64748B' },
    { name: 'Fuchsia', value: '#D946EF' }, { name: 'Lime', value: '#84CC16' },
  ];

  const loadPlanning = async () => {
    if (!member.user) return;
    setPlanningLoading(true);
    try {
      const [as, ch] = await Promise.all([
        pb.collection('assignments').getFullList({ filter: `user = "${member.user}"`, expand: 'chantier', sort: '-start_date' }),
        pb.collection('chantiers').getFullList({ sort: 'title' })
      ]);
      setAssignments(as);
      setAllChantiers(ch);
    } catch (e) { console.error(e); }
    finally { setPlanningLoading(false); }
  };

  useEffect(() => {
    if (activeTab === 'planning') loadPlanning();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const handleColorChange = async (newColor) => {
    if (!member.user) return;
    try {
      const updated = await pb.collection('users').update(member.user, { color: newColor });
      setUserData(updated);
    } catch (e) { setError('Erreur lors du changement de couleur.'); }
  };

  const handleAssign = async () => {
    if (!formAssign.chantier || !formAssign.start_date) return;
    setSavingAssign(true);
    try {
      await pb.collection('assignments').create({
        user: member.user,
        chantier: formAssign.chantier,
        start_date: new Date(formAssign.start_date).toISOString(),
        end_date: formAssign.end_date ? new Date(formAssign.end_date).toISOString() : new Date(formAssign.start_date).toISOString(),
        notes: formAssign.notes
      });
      setFormAssign({ chantier: '', start_date: '', end_date: '', notes: '' });
      setShowAssignForm(false);
      loadPlanning();
    } catch { setError("Erreur lors de l'assignation."); }
    finally { setSavingAssign(false); }
  };

  const handleRemoveAssign = async (id) => {
    if (!window.confirm('Retirer cette affectation ?')) return;
    try { await pb.collection('assignments').delete(id); loadPlanning(); }
    catch { setError('Erreur lors de la suppression.'); }
  };

  const filteredChantiers = allChantiers.filter(c =>
    !searchChantier || c.title?.toLowerCase().includes(searchChantier.toLowerCase()) || c.client_name?.toLowerCase().includes(searchChantier.toLowerCase())
  );

  const fileInputRef = useRef(null);

  const compressImage = (file, maxWidth = 600, quality = 0.8) => {
    return new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      img.onload = () => {
        try {
          let { width, height } = img;
          if (width > maxWidth) { height = (height * maxWidth) / width; width = maxWidth; }
          canvas.width = width; canvas.height = height;
          ctx.drawImage(img, 0, 0, width, height);
          canvas.toBlob((blob) => blob ? resolve(blob) : reject(new Error('Compression error')), 'image/jpeg', quality);
        } catch (err) { reject(err); }
      };
      img.onerror = () => reject(new Error("Image error"));
      img.src = URL.createObjectURL(file);
    });
  };

  const handlePhotoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const compressed = await compressImage(file);
      const finalFile = new File([compressed], file.name, { type: 'image/jpeg' });
      setPhoto({ type: 'new', file: finalFile });
    } catch (err) { setError("Erreur photo"); }
  };

  const onSave = async (e) => {
    e.preventDefault(); setIsSaving(true); setError('');
    try {
      let userId = member.user;
      if (access.active) {
        const uData = { 
          email: access.email, 
          role: access.role, 
          name: name,
          can_view_all_planning: access.can_view_all_planning,
          can_manage_team: access.can_manage_team,
          can_edit_chantiers: access.can_edit_chantiers,
          password: access.password || undefined, 
          passwordConfirm: access.password || undefined, 
          emailVisibility: true 
        };
        if (userId) await pb.collection('users').update(userId, uData);
        else {
          if (!access.password) throw new Error("Mot de passe requis pour la création.");
          const newUser = await pb.collection('users').create({ ...uData, username: access.email.split('@')[0] + Math.floor(Math.random()*1000) });
          userId = newUser.id;
        }
      }

      const formData = new FormData();
      formData.append('name', name);
      formData.append('role', role);
      formData.append('bio', bio);
      formData.append('published', published);
      if (userId) formData.append('user', userId);
      if (photo && photo.type === 'new') formData.append('photo', photo.file);
      else if (!photo) formData.append('photo', '');

      if (member.id) await pb.collection('team_members').update(member.id, formData);
      else await pb.collection('team_members').create(formData);
      onSaved();
    } catch (err) { setError(err.message || 'Erreur lors de la sauvegarde'); } 
    finally { setIsSaving(false); }
  };

  const shareWhatsApp = () => {
     const text = `Salut ${name} !\n\nVoici tes accès pour le planning AEF 32 :\n🔗 Dashboard : ${window.location.origin}\n📧 Email : ${access.email}\n🔑 Mot de passe : ${access.password || 'Inchangé'}`;
     window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
  };

  const previewUrl = photo ? (photo.type === 'existing' ? getFileUrl(member, photo.name) : URL.createObjectURL(photo.file)) : '';

  return (
    <div className="admin-modal-overlay">
      <div className="admin-modal-container studio-team animation-slide-up">
        <header className="modal-header-premium">
          <div className="header-info">
             <div className="header-tag">{member.id ? 'Fiche Salarié' : 'Nouvelle Recrue'}</div>
             <h2>Studio Collaborateur</h2>
          </div>
          <button className="btn-close-modal" onClick={onClose}><X size={20}/></button>
        </header>

        <div className="modal-tabs">
          <button className={activeTab === 'profile' ? 'active' : ''} onClick={() => setActiveTab('profile')}>
            <User size={16}/> <span>Compétences</span>
          </button>
          <button className={activeTab === 'access' ? 'active' : ''} onClick={() => setActiveTab('access')}>
            <Smartphone size={16}/> <span>Accès Mobile</span>
          </button>
          <button className={activeTab === 'planning' ? 'active' : ''} onClick={() => setActiveTab('planning')}>
            <span style={{ fontSize: 14 }}>🎨</span> <span>Planning & Couleur</span>
          </button>
        </div>

        <form onSubmit={onSave} className="modal-body-premium">
           {error && <div className="modal-error"><AlertCircle size={14}/> {error}</div>}

           <div className="tab-panels">
              {activeTab === 'profile' && (
                <div className="tab-pane active animation-fade-in">
                   {!member.id && (
                     <div className="link-account-box" style={{ marginBottom: 25, position: 'relative' }}>
                       <label style={{ fontSize: 11, fontWeight: 800, color: '#0ea5e9', textTransform: 'uppercase', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                         <Users size={14}/> Lier un compte existant (Client/Salarié)
                       </label>
                       <div style={{ position: 'relative' }}>
                         <Search size={16} style={{ position: 'absolute', left: 14, top: '48%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
                         <input 
                           placeholder="Rechercher par nom ou email..." 
                           value={userSearchText} 
                           onChange={e => fetchUsersToLink(e.target.value)}
                           style={{ width: '100%', padding: '12px 14px 12px 42px', borderRadius: 16, border: '2px solid #e0f2fe', background: '#f0f9ff', fontWeight: 700, fontSize: 13, boxSizing: 'border-box' }}
                         />
                       </div>
                       {showUserDropdown && existingUsers.length > 0 && (
                         <div className="search-dropdown-premium" style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 16, marginTop: 8, boxShadow: '0 20px 40px -10px rgba(0,0,0,0.1)', zIndex: 100, maxHeight: 200, overflowY: 'auto', padding: 8 }}>
                            {existingUsers.map(u => (
                              <button key={u.id} type="button" onClick={() => handleSelectExistingUser(u)} style={{ width: '100%', padding: '10px 14px', borderRadius: 10, border: 'none', background: 'transparent', textAlign: 'left', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10, transition: 'all 0.2s' }} className="dropdown-item-hover">
                                 <div style={{ width: 28, height: 28, borderRadius: 8, background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 900, color: '#64748b' }}>{(u.name || u.username).slice(0, 1)}</div>
                                 <div style={{ flex: 1, minWidth: 0 }}>
                                   <div style={{ fontSize: 13, fontWeight: 800, color: '#0f172a' }}>{u.name || u.username}</div>
                                   <div style={{ fontSize: 11, color: '#94a3b8', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{u.email}</div>
                                 </div>
                                 <Plus size={14} color="#0ea5e9" />
                              </button>
                            ))}
                         </div>
                       )}
                     </div>
                   )}
                   <div className="visual-studio" style={{ marginBottom: 25 }}>
                      <div className="preview-main-box" style={{ borderRadius: '50%' }}>
                         {previewUrl ? <img src={previewUrl} alt="Avatar" style={{ borderRadius: '50%' }} /> : <User size={48} color="#cbd5e1" />}
                      </div>
                      <div className="visual-actions">
                         <label className="btn-upload-premium">
                            <Camera size={18}/> <span>Changer la photo de profil</span>
                            <input type="file" hidden accept="image/*" onChange={handlePhotoChange} />
                         </label>
                         {photo && <button type="button" className="btn-remove-media" onClick={() => setPhoto(null)}><Trash2 size={16}/> Supprimer</button>}
                         <p className="field-hint">Photo carrée recommandée pour un rendu optimal.</p>
                      </div>
                   </div>
                   <div className="form-group-premium">
                      <label>Nom & Prénom</label>
                      <input value={name} onChange={e => setName(e.target.value)} required placeholder="ex: Julien Delacroix" />
                   </div>
                   <div className="form-group-premium">
                      <label>Intitulé du poste</label>
                      <input value={role} onChange={e => setRole(e.target.value)} placeholder="ex: Spécialiste Isolation RGE" />
                   </div>
                   <div className="form-group-premium">
                      <label>Note interne / Bio</label>
                      <textarea rows={3} value={bio} onChange={e => setBio(e.target.value)} placeholder="Quelques mots sur le parcours du collaborateur..." />
                   </div>
                   <div className="toggle-switch-group">
                      <span>Visible sur la page Équipe du site</span>
                      <label className="switch">
                         <input type="checkbox" checked={published} onChange={e => setPublished(e.target.checked)} />
                         <span className="slider round"></span>
                      </label>
                   </div>
                </div>
              )}

              {activeTab === 'access' && (
                <div className="tab-pane active animation-fade-in">
                   <div className="access-config-card">
                      <div className="toggle-switch-group" style={{ background: 'transparent', padding: 0, marginBottom: 25 }}>
                         <div>
                            <div style={{ fontWeight: 900, color: '#0f172a' }}>Autoriser la connexion aux outils</div>
                            <p className="field-hint">Permet de voir son planning sur smartphone.</p>
                         </div>
                         <label className="switch">
                            <input type="checkbox" checked={access.active} onChange={e => setAccess({...access, active: e.target.checked})} />
                            <span className="slider round"></span>
                         </label>
                      </div>
                      {access.active && (
                        <div className="access-form-grid animation-slide-up">
                           <div className="form-group-premium">
                              <label>Email professionnel</label>
                              <div style={{ position: 'relative' }}>
                                 <Mail size={16} style={{ position: 'absolute', left: 16, top: 16, color: '#94a3b8' }} />
                                 <input type="email" value={access.email} onChange={e => setAccess({...access, email: e.target.value})} style={{ paddingLeft: 45 }} placeholder="email@aef32.com" required />
                              </div>
                           </div>
                           <div className="form-group-premium">
                              <label>{member.id ? 'Définir un nouveau mot de passe' : 'Créer un mot de passe'}</label>
                              <div style={{ position: 'relative' }}>
                                 <Lock size={16} style={{ position: 'absolute', left: 16, top: 16, color: '#94a3b8' }} />
                                 <input type="password" value={access.password} onChange={e => setAccess({...access, password: e.target.value})} style={{ paddingLeft: 45 }} placeholder="Min 8 caractères..." />
                              </div>
                              <p className="field-hint">Laissez vide pour conserver l'ancien.</p>
                           </div>
                           <div className="form-group-premium">
                              <label>Type de profil administratif</label>
                              <select value={access.role} onChange={e => setAccess({...access, role: e.target.value})}>
                                 <option value="worker">Technicien / Terrain</option>
                                 <option value="commercial">Commercial / Suivi</option>
                                 <option value="admin">Administrateur Total</option>
                              </select>
                           </div>

                           <div style={{ marginTop: 20, marginBottom: 10, fontSize: 12, fontWeight: 900, color: '#94a3b8', textTransform: 'uppercase' }}>
                             Privilèges d'application mobile
                           </div>

                           <div className="toggle-switch-group" style={{ background: '#fff', padding: '16px', borderRadius: '12px', border: '1px solid #e2e8f0', marginBottom: 12 }}>
                              <div>
                                 <div style={{ fontWeight: 800, fontSize: 13, color: '#0f172a' }}>Vue globale (Tous chantiers)</div>
                                 <p className="field-hint" style={{ margin: '2px 0 0', fontSize: 11 }}>Accès total au planning de l'équipe sur mobile.</p>
                              </div>
                              <label className="switch">
                                 <input type="checkbox" checked={access.can_view_all_planning} onChange={e => setAccess({...access, can_view_all_planning: e.target.checked})} />
                                 <span className="slider round"></span>
                              </label>
                           </div>

                           <div className="toggle-switch-group" style={{ background: '#fff', padding: '16px', borderRadius: '12px', border: '1px solid #e2e8f0', marginBottom: 12 }}>
                              <div>
                                 <div style={{ fontWeight: 800, fontSize: 13, color: '#0f172a' }}>Gestion d'Équipe</div>
                                 <p className="field-hint" style={{ margin: '2px 0 0', fontSize: 11 }}>Peut assigner et retirer des missions depuis son app.</p>
                              </div>
                              <label className="switch">
                                 <input type="checkbox" checked={access.can_manage_team} onChange={e => setAccess({...access, can_manage_team: e.target.checked})} />
                                 <span className="slider round"></span>
                              </label>
                           </div>

                           <div className="toggle-switch-group" style={{ background: '#fff', padding: '16px', borderRadius: '12px', border: '1px solid #e2e8f0', marginBottom: 12 }}>
                              <div>
                                 <div style={{ fontWeight: 800, fontSize: 13, color: '#0f172a' }}>Modification Chantiers</div>
                                 <p className="field-hint" style={{ margin: '2px 0 0', fontSize: 11 }}>Mise à jour libre du statut des chantiers (sur app).</p>
                              </div>
                              <label className="switch">
                                 <input type="checkbox" checked={access.can_edit_chantiers} onChange={e => setAccess({...access, can_edit_chantiers: e.target.checked})} />
                                 <span className="slider round"></span>
                              </label>
                           </div>
                           <button type="button" className="btn-whatsapp-premium" onClick={shareWhatsApp}>
                              <Smartphone size={20}/> <span>Envoyer les accès par WhatsApp</span>
                           </button>
                        </div>
                      )}
                   </div>
                </div>
              )}

              {activeTab === 'planning' && (
                <div className="tab-pane active animation-fade-in" style={{ minHeight: 300 }}>
                  {!member.user ? (
                    <div style={{ textAlign: 'center', padding: '40px 20px', color: '#94a3b8' }}>
                      <div style={{ fontSize: 40, marginBottom: 12 }}>🔗</div>
                      <p style={{ fontWeight: 700, color: '#64748b' }}>Aucun compte lié</p>
                      <p style={{ fontSize: 13 }}>Activez l'accès mobile dans l'onglet "Accès Mobile" pour gérer le planning.</p>
                    </div>
                  ) : (
                    <>
                      <div style={{ marginBottom: 24 }}>
                        <div style={{ fontSize: 11, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', marginBottom: 10, letterSpacing: 1 }}>Couleur de l'Intervenant sur le Planning</div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                          {PRESET_COLORS.map(c => (
                            <button key={c.value} type="button" onClick={() => handleColorChange(c.value)}
                              style={{ 
                                width: 30, height: 30, borderRadius: '50%',
                                border: userData.color === c.value ? '3px solid #fff' : '2px solid transparent',
                                boxShadow: userData.color === c.value ? `0 0 0 2px ${c.value}` : '0 2px 4px rgba(0,0,0,0.1)',
                                background: c.value, cursor: 'pointer', transition: 'all 0.2s',
                                transform: userData.color === c.value ? 'scale(1.2)' : 'scale(1)'
                              }}
                              title={c.name}
                            />
                          ))}
                        </div>
                        {userData.color && (
                          <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
                            <div style={{ width: 12, height: 12, borderRadius: '50%', background: userData.color }} />
                            <span style={{ fontSize: 12, color: '#64748b', fontWeight: 700 }}>Couleur active : {userData.color}</span>
                          </div>
                        )}
                      </div>

                      <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: 20 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                          <div style={{ fontWeight: 900, fontSize: 15, color: '#0f172a' }}>Chantiers assignés ({assignments.length})</div>
                          <button type="button" onClick={() => setShowAssignForm(v => !v)} style={{ background: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0', borderRadius: 10, padding: '7px 14px', fontWeight: 800, fontSize: 12, cursor: 'pointer' }}>
                            {showAssignForm ? '✕ Annuler' : '+ Assigner un chantier'}
                          </button>
                        </div>

                        {showAssignForm && (
                          <div style={{ background: '#f8fafc', borderRadius: 16, padding: 16, border: '1px solid #e2e8f0', marginBottom: 16, display: 'grid', gap: 12 }}>
                            <div>
                              <label style={{ fontSize: 11, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', display: 'block', marginBottom: 6 }}>Rechercher un chantier</label>
                              <input value={searchChantier} onChange={e => setSearchChantier(e.target.value)} placeholder="Nom ou client..." style={{ width: '100%', padding: '10px 14px', borderRadius: 12, border: '1px solid #e2e8f0', fontSize: 13, boxSizing: 'border-box' }} />
                            </div>
                            <div>
                              <label style={{ fontSize: 11, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', display: 'block', marginBottom: 6 }}>Chantier *</label>
                              <select value={formAssign.chantier} onChange={e => setFormAssign({ ...formAssign, chantier: e.target.value })} style={{ width: '100%', padding: '10px 14px', borderRadius: 12, border: '1px solid #e2e8f0', fontSize: 13 }}>
                                <option value="">Sélectionner...</option>
                                {filteredChantiers.map(c => <option key={c.id} value={c.id}>{c.title}{c.client_name ? ` — ${c.client_name}` : ''}</option>)}
                              </select>
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                              <div>
                                <label style={{ fontSize: 11, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', display: 'block', marginBottom: 6 }}>Du *</label>
                                <input type="datetime-local" value={formAssign.start_date} onChange={e => setFormAssign({ ...formAssign, start_date: e.target.value })} style={{ width: '100%', padding: '10px', borderRadius: 12, border: '1px solid #e2e8f0', fontSize: 12, boxSizing: 'border-box' }} />
                              </div>
                              <div>
                                <label style={{ fontSize: 11, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', display: 'block', marginBottom: 6 }}>Au</label>
                                <input type="datetime-local" value={formAssign.end_date} onChange={e => setFormAssign({ ...formAssign, end_date: e.target.value })} style={{ width: '100%', padding: '10px', borderRadius: 12, border: '1px solid #e2e8f0', fontSize: 12, boxSizing: 'border-box' }} />
                              </div>
                            </div>
                            <button type="button" onClick={handleAssign} disabled={savingAssign} style={{ background: '#0f172a', color: '#fff', border: 'none', borderRadius: 12, padding: '12px', fontWeight: 800, fontSize: 14, cursor: 'pointer' }}>
                              {savingAssign ? 'Assignation...' : '✓ Confirmer'}
                            </button>
                          </div>
                        )}

                        {planningLoading && <div style={{ textAlign: 'center', padding: 30, color: '#94a3b8' }}>Chargement...</div>}

                        {!planningLoading && assignments.length === 0 && !showAssignForm && (
                          <div style={{ textAlign: 'center', padding: '30px 20px', color: '#94a3b8' }}>
                            <div style={{ fontSize: 36, marginBottom: 8 }}>🏗️</div>
                            <p style={{ margin: 0, fontWeight: 600, color: '#64748b' }}>Aucun chantier assigné</p>
                            <p style={{ margin: '4px 0 0', fontSize: 13 }}>Utilisez "+ Assigner un chantier".</p>
                          </div>
                        )}

                        {!planningLoading && assignments.map(a => {
                          const ch = a.expand?.chantier;
                          return (
                            <div key={a.id} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 14, padding: '12px 14px', display: 'flex', gap: 12, alignItems: 'flex-start', marginBottom: 8 }}>
                              <div style={{ width: 8, height: 8, borderRadius: 2, flexShrink: 0, marginTop: 5, background: ch?.status === 'completed' ? '#22c55e' : '#3b82f6' }} />
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontWeight: 800, fontSize: 13, color: '#0f172a' }}>{ch?.title || '—'}</div>
                                {ch?.client_name && <div style={{ fontSize: 12, color: '#64748b' }}>{ch.client_name}</div>}
                                <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>📅 {new Date(a.start_date).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })}</div>
                              </div>
                              <button type="button" onClick={() => handleRemoveAssign(a.id)} style={{ width: 28, height: 28, border: 'none', background: '#fff1f2', borderRadius: 8, color: '#f43f5e', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                <Trash2 size={13} />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </>
                  )}
                </div>
              )}
           </div>

            <footer className="modal-footer-premium">
              <button type="button" className="btn-modal-cancel" onClick={onClose}>Annuler</button>
              <button type="submit" className="btn-modal-save" disabled={isSaving}>
                {isSaving ? <RefreshCcw size={18} className="spin"/> : <Save size={18}/>}
                <span>{member.id ? 'Sauvegarder les modifications' : 'Créer le profil'}</span>
              </button>
            </footer>
        </form>
      </div>

      <style>{`
        .access-config-card { background: #f8fafc; padding: 25px; border-radius: 24px; border: 1px solid #f1f5f9; }
        .btn-whatsapp-premium { width: 100%; padding: 16px; border: none; background: #22c55e; color: #fff; border-radius: 16px; font-weight: 800; display: flex; align-items: center; justify-content: center; gap: 10px; cursor: pointer; transition: all 0.2s; margin-top: 10px; }
        .btn-whatsapp-premium:hover { background: #16a34a; transform: translateY(-2px); box-shadow: 0 10px 15px -3px rgba(34, 197, 94, 0.3); }
        .modal-error { background: #fee2e2; color: #b91c1c; padding: 12px 18px; border-radius: 14px; margin-bottom: 20px; font-size: 13px; font-weight: 700; display: flex; align-items: center; gap: 10px; }
        .dropdown-item-hover:hover { background: #f8fafc !important; }
      `}</style>
    </div>
  );
}

